import { Inset, StackChildren, getFocusStyles } from '@doordash/design-language';
import styled, { css } from 'styled-components';
import { IconButton } from '@doordash/component-button';
import Separator from '@doordash/component-separator';
import { ListCellSide } from './types.js';

var getColumns = function getColumns(_ref) {
  var hasBeforeContent = _ref.hasBeforeContent,
      hasSecondaryTitleStart = _ref.hasSecondaryTitleStart,
      hasIconStart = _ref.hasIconStart,
      hasIconEnd = _ref.hasIconEnd,
      hasSecondaryTitleEnd = _ref.hasSecondaryTitleEnd,
      hasAfterContent = _ref.hasAfterContent,
      shouldTruncateSubContent = _ref.shouldTruncateSubContent;
  var columns = [];
  var primaryContentColumnString = shouldTruncateSubContent ? 'minmax(50%, 1fr)' : '1fr';
  columns.push(primaryContentColumnString);
  var subContentColumnString = shouldTruncateSubContent ? 'minmax(0, auto)' : 'auto';

  if (hasBeforeContent) {
    columns.unshift(subContentColumnString);
  }

  if (hasSecondaryTitleStart) {
    columns.unshift(subContentColumnString);
  }

  if (hasIconStart) {
    columns.unshift('auto');
  }

  if (hasAfterContent) {
    columns.push(subContentColumnString);
  }

  if (hasSecondaryTitleEnd) {
    columns.push(subContentColumnString);
  }

  if (hasIconEnd) {
    columns.push('auto');
  }

  return columns.join(' ');
};

var ListCellContentContainer = styled(Inset).attrs(function (props) {
  return {
    size: props.insetSize,
    horizontalSize: props.insetHorizontalSize,
    verticalSize: props.insetVerticalSize
  };
}).withConfig({
  displayName: "styles__ListCellContentContainer",
  componentId: "sc-7lv6ab-0"
})(["max-width:100%;width:100%;display:grid;grid-template-rows:auto auto auto;grid-template-columns:", ";grid-column-gap:", "px;@media all and (-ms-high-contrast:none),(-ms-high-contrast:active){display:flex;& > * + *{margin-left:", "px !important;}}"], getColumns, function (props) {
  return props.columnGapSize;
}, function (props) {
  return props.columnGapSize;
});
var MiddleContainer = styled(StackChildren).attrs(function () {
  return {
    size: StackChildren.Sizes.None
  };
}).withConfig({
  displayName: "styles__MiddleContainer",
  componentId: "sc-7lv6ab-1"
})(["flex-grow:1;overflow:hidden;flex-basis:100%;grid-row:2;", ";"], function (props) {
  return css(["grid-column:", ";"], 1 + props.hasBeforeContent + props.hasIconStart + props.hasSecondaryTitleStart);
});
var SideContainer = styled.div.withConfig({
  displayName: "styles__SideContainer",
  componentId: "sc-7lv6ab-2"
})(["flex-shrink:0;align-self:", ";grid-row:2;", ";"], function (props) {
  return props.alignment;
}, function (props) {
  return css(["grid-column:", ";"], (props.side === ListCellSide.Start ? 1 : 2 + props.hasSecondaryTitleStart) + props.hasBeforeContent + props.hasIconStart);
});
var ListCellContainer = styled.div.withConfig({
  displayName: "styles__ListCellContainer",
  componentId: "sc-7lv6ab-3"
})(["position:relative;", ";"], function (props) {
  return (props.onClick || props.href) && css(["cursor:pointer;overflow:hidden;text-decoration:none;appearance:none;border:none;outline:none !important;padding:0;margin:0;text-align:inherit;display:block;width:100%;background:", ";&:hover{background:", ";}&:active{background:", ";}&:focus{position:relative;z-index:1;background:", ";", ";}"], props.themeColors.background, props.themeColors.hovered, props.themeColors.pressed, props.themeColors.hovered, getFocusStyles(props.focusStyleProps));
});

var getIconButtonAdjustment = function getIconButtonAdjustment(size) {
  switch (size) {
    case IconButton.Sizes.Small:
      return -4;

    case IconButton.Sizes.Medium:
    default:
      return -8;
  }
};

var IconContainer = styled.div.withConfig({
  displayName: "styles__IconContainer",
  componentId: "sc-7lv6ab-4"
})(["grid-row:2;", ";flex-shrink:0;line-height:0;display:flex;align-items:", ";margin:", "px;", ";> svg{", ";}"], function (_ref2) {
  var side = _ref2.side,
      columnProps = _ref2.columnProps;
  return css(["grid-column:", "};"], columnProps.hasBeforeContent + (side === ListCellSide.Start ? 1 : 2 + columnProps.hasIconStart + columnProps.hasSecondaryTitleEnd + columnProps.hasSecondaryTitleStart));
}, function (props) {
  return props.iconAlignment;
}, function (props) {
  return props.shouldBeButton ? getIconButtonAdjustment(props.size) : 0;
}, function (props) {
  return (// This is to solve an issue with the white background color in the plain button type (the only button type supported today)
    props.shouldBeButton && css(["> button{background:transparent;}"])
  );
}, function (props) {
  return props.renderIconSpacer && 'visibility: hidden;';
});
var GridSeparatorContainer = styled.div.withConfig({
  displayName: "styles__GridSeparatorContainer",
  componentId: "sc-7lv6ab-5"
})(["grid-row:-1;grid-column-start:", ";grid-column-end:", ";margin-right:", ";", ";"], function (props) {
  return props.gridColumnStart;
}, function (props) {
  return props.type ? -1 : 'none';
}, function (props) {
  return "-".concat(props.insetHorizontalSize, "px");
}, function (_ref3) {
  var type = _ref3.type,
      isSquished = _ref3.isSquished,
      insetHorizontalSize = _ref3.insetHorizontalSize,
      insetVerticalSize = _ref3.insetVerticalSize;
  return css(["margin-left:", "px;margin-bottom:", "px;margin-top:", "px;"], type === Separator.Types.InsetBorder ? 0 : -insetHorizontalSize, isSquished ? -insetVerticalSize / 2 : -insetVerticalSize, isSquished ? insetVerticalSize / 2 - 1 : insetVerticalSize - 1);
});

export { GridSeparatorContainer, IconContainer, ListCellContainer, ListCellContentContainer, MiddleContainer, SideContainer };
